import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  getStudentsBasicYear,
  getStudentStays,
  linkAgentStay,
  updateAgentStay,
} from "./api/agentsApi";
import CommissionFields, { rateIsRequired, rateIsValid } from "./CommissionFields";

// One row of `api/studentsBasicYear` as the picker needs it. That feed carries
// the forename as `studentName` and the surname separately as `studentSurname`,
// so both have to be pulled out by hand — `label` is what's shown and typed
// back, `search` is what's matched against.
export const studentOptionOf = (s) => {
  const id = s.id ?? s.studentId;
  const forename = String(s.studentName || "").trim();
  const surname = String(s.studentSurname || "").trim();
  const name = [surname, forename].filter(Boolean).join(", ");
  return {
    id,
    label: name ? `${name} (#${id})` : `#${id}`,
    search: `${forename} ${surname} ${id}`.toLowerCase(),
  };
};

// Match every whitespace-separated term against forename, surname and id, in any
// order — so "smith", "13202" and "smith alice" all find the same student. MUI's
// default filter only substring-matches the option label, which would miss both
// the id and a surname-first query.
export const filterStudents = (options, { inputValue }) => {
  const terms = String(inputValue || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return options;
  return options.filter((o) => terms.every((t) => o.search.includes(t)));
};

// Add a per-stay connection (pick student → stay → commission, defaulting from
// the agent) or edit an existing connection's snapshot (student/stay fixed).
export default function ConnectionDialog({ open, onClose, mode, agent, connection, year, existingStayIds, onSaved }) {
  const isEdit = mode === "edit";

  // Stays already linked to this agent — disabled in the picker so we don't
  // attempt a duplicate link (the backend 409s on it).
  const linkedStayIds = useMemo(
    () => new Set((existingStayIds || []).map((id) => String(id))),
    [existingStayIds]
  );

  const [students, setStudents] = useState([]);
  const [student, setStudent] = useState(null);
  const [stays, setStays] = useState([]);
  const [stayId, setStayId] = useState("");

  const [commission, setCommission] = useState("GROSS");
  const [commissionRate, setCommissionRate] = useState("20");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    if (isEdit && connection) {
      setCommission(String(connection.commission || "GROSS").toUpperCase());
      setCommissionRate(connection.commissionRate != null ? String(connection.commissionRate) : "0");
    } else {
      setStudent(null);
      setStays([]);
      setStayId("");
      // Default the snapshot from the agent.
      setCommission(String(agent?.commission || "GROSS").toUpperCase());
      setCommissionRate(agent?.commissionRate != null ? String(agent.commissionRate) : "20");
      getStudentsBasicYear(year)
        .then((data) => setStudents(Array.isArray(data) ? data : []))
        .catch(() => setStudents([]));
    }
  }, [open, isEdit, connection, agent, year]);

  // Student picker options, normalised once so the label, the sort and the search
  // all read the same fields. `api/studentsBasicYear` carries the forename as
  // `studentName` and the surname separately as `studentSurname`.
  const studentOptions = useMemo(
    () =>
      (students || [])
        .map(studentOptionOf)
        .filter((o) => o.id != null)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [students]
  );

  // When a student is picked, load their stays.
  useEffect(() => {
    if (isEdit || !student?.id) return;
    setStays([]);
    setStayId("");
    getStudentStays(student.id)
      .then((data) => setStays(Array.isArray(data) ? data : []))
      .catch(() => setStays([]));
  }, [student, isEdit]);

  const canSave = useMemo(() => {
    if (!rateIsValid(commission, commissionRate)) return false;
    if (isEdit) return true;
    return !!student && !!stayId;
  }, [commission, commissionRate, isEdit, student, stayId]);

  const body = () => ({
    commission,
    commissionRate: rateIsRequired(commission) ? Number(commissionRate) : 0,
  });

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateAgentStay(agent.id, connection.stayId, body());
      } else {
        await linkAgentStay(agent.id, stayId, body());
      }
      await onSaved?.();
      onClose?.();
    } catch (e) {
      setError(
        e.status === 409
          ? new Error("That stay is already linked to this agent.")
          : e
      );
    } finally {
      setSaving(false);
    }
  };

  const stayLabel = (s) =>
    `${s.arrivalDate} – ${s.departureDate}${s.campus ? ` · ${s.campus}` : ""}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit connection" : "Link a stay"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          {isEdit ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {connection?.studentName} · {connection?.arrivalDate} – {connection?.departureDate}
              {connection?.campus ? ` · ${connection.campus}` : ""}
            </Typography>
          ) : (
            <>
              <Autocomplete
                options={studentOptions}
                value={student}
                onChange={(_, v) => setStudent(v)}
                getOptionLabel={(o) => o.label || ""}
                filterOptions={filterStudents}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Find student (${year})`}
                    placeholder="Surname, first name or ID"
                    size="small"
                  />
                )}
              />

              <FormControl size="small" fullWidth disabled={!student}>
                <InputLabel id="stay-select">Stay</InputLabel>
                <Select
                  labelId="stay-select"
                  label="Stay"
                  value={stayId}
                  onChange={(e) => setStayId(e.target.value)}
                >
                  {stays.length === 0 ? (
                    <MenuItem value="" disabled>
                      {student ? "No stays for this student" : "Pick a student first"}
                    </MenuItem>
                  ) : (
                    stays.map((s) => {
                      const linked = linkedStayIds.has(String(s.stayId));
                      return (
                        <MenuItem key={s.stayId} value={String(s.stayId)} disabled={linked}>
                          {stayLabel(s)}
                          {linked ? " — already linked" : ""}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
            </>
          )}

          <CommissionFields
            idPrefix="conn"
            commission={commission}
            onCommissionChange={setCommission}
            rate={commissionRate}
            onRateChange={setCommissionRate}
          />

          {!isEdit ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Defaults from the agent ({agent?.commission} {agent?.commissionRate}%); change for a
              per-stay rate. Snapshotted at creation.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? "Saving…" : isEdit ? "Save" : "Link stay"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
